
'use client';

import React, { useRef, useEffect } from 'react';
import { Search, Loader2, X } from "lucide-react";

interface SearchInputProps {
  onSearchFocus?: () => void;
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchClick?: () => void;
  isSearching?: boolean;
  registerSearchInput?: (ref: HTMLInputElement | null) => void;
}

const SearchInput: React.FC<SearchInputProps> = ({ 
  onSearchFocus,
  placeholder = "Search movies, directors, or actors...",
  className = "",
  value,
  onChange,
  onClear,
  onKeyDown,
  onSearchClick,
  isSearching = false,
  registerSearchInput
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Connect the input ref to the focus hook via props
  useEffect(() => {
    if (inputRef.current && registerSearchInput) {
      console.log("Registering search input with focus hook");
      registerSearchInput(inputRef.current);
    }
    
    return () => {
      if (registerSearchInput) {
        registerSearchInput(null);
      }
    };
  }, [registerSearchInput]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleClearClick = () => {
    console.log("Clear button clicked");
    if (onClear) onClear();
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleSearchClick = () => {
    if (onSearchClick && value && value.trim()) {
      onSearchClick();
    }
  };

  const isControlled = value !== undefined && onChange !== undefined;
  const hasText = value && value.length > 0;

  return (
    <div className={`relative w-full max-w-2xl ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full rounded-full border border-mono-slateGray/70 bg-mono-eerieBlack text-mono-platinum placeholder-mono-frenchGray2/60 py-2 pl-4 focus:outline-none transition duration-200 ${
            hasText ? 'pr-20' : 'pr-12'
          }`}
          placeholder={placeholder}
          value={isControlled ? value : undefined}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          onFocus={onSearchFocus}
          aria-label="Search for movies"
          autoComplete="off"
        />
        
        {hasText && onClear && (
          <div 
            className="absolute inset-y-0 right-12 flex items-center cursor-pointer"
            onClick={handleClearClick}
            aria-label="Clear search"
          >
            <X className="h-6 w-6 text-mono-frenchGray hover:text-mono-platinum transition-colors" />
          </div>
        )}
        
        <div 
          className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
            hasText && onSearchClick ? 'cursor-pointer hover:text-mono-platinum' : ''
          }`}
          onClick={hasText ? handleSearchClick : undefined}
          aria-label={hasText ? "Search" : undefined}
        >
          {isSearching ? (
            <Loader2 className="h-5 w-5 text-mono-frenchGray animate-spin" />
          ) : (
            <Search className={`h-5 w-5 text-mono-frenchGray transition-colors ${
              hasText && onSearchClick ? 'hover:text-mono-platinum' : ''
            }`} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchInput;
